import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, MessageCircle, Shield } from 'lucide-react';
import { businessConfig } from '../config/business';

export const Footer = () => {
  const { company, contact, insurance, social, assets, reviews } = businessConfig;

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Service Areas', path: '/areas' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Get a Quote', path: '/quote' }
  ];

  const serviceLinks = [
    { name: 'House Removals', path: '/services#house-removals' },
    { name: 'Apartment Removals', path: '/services#apartment-removals' },
    { name: 'Office Removals', path: '/services#office-removals' },
    { name: 'Furniture Removals', path: '/services#furniture-removals' },
    { name: 'Packing Services', path: '/services#packing' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {/* Company Info */}
          <div>
            <img
              src={assets.logo}
              alt="Thrifty Move"
              className="h-16 mb-4"
            />
            <p className="text-gray-400 text-sm mb-4">
              {company.description}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <Shield className="w-4 h-4 text-yellow-500" />
              <span>{insurance.description}</span>
            </div>
            <div className="flex gap-3 mt-4">
              {social.facebook && (
                <a
                  href={social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-yellow-500 hover:text-gray-900 transition-colors duration-200"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {social.instagram && (
                <a
                  href={social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-yellow-500 hover:text-gray-900 transition-colors duration-200"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4">Our Services</h3>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div>
            <h3 className="text-lg font-bold mb-4">Contact Us</h3>
            <div className="space-y-3">
              <a 
                href={contact.phoneLink}
                className="flex items-center gap-3 text-gray-400 hover:text-yellow-400 transition-colors duration-200"
                data-phone-number="true"
              >
                <Phone className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <span data-phone-number="true">{contact.phone}</span>
              </a>
              <a 
                href={contact.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-400 hover:text-yellow-400 transition-colors duration-200"
              >
                <MessageCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <span>WhatsApp: {contact.whatsapp}</span>
              </a>
              <a 
                href={`mailto:${contact.email}`}
                className="flex items-center gap-3 text-gray-400 hover:text-yellow-400 transition-colors duration-200"
              >
                <Mail className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <span>{contact.email}</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span>{contact.location}</span>
                  <p className="text-sm text-gray-500">{contact.serviceArea}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-sm text-center md:text-left">
              <p className="font-medium">{company.legalName} trading as {company.tradingAs}</p>
              <p>ABN: {company.abn}</p>
              <p className="mt-1">© {new Date().getFullYear()} {company.name}. All rights reserved.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/policies" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200">
                FAQ & Policies
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/contact" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
